import {
  allowedMonitoringPromptKpis,
  MonitoringPromptKpi,
  MonitoringPromptPurposeType,
  SyncCoreDataDefinitionsEnumTranslator,
} from '@edgebox/sync-core-data-definitions';
import { AiKpi } from '../GovernanceAnalyticsAiVisibility.utils';
import { TONE_KPI } from '../toneMetric';

/**
 * Which KPIs the analysis screens may render for the purpose in view.
 *
 * The rule itself lives in the data definitions (`allowedMonitoringPromptKpis`), because it is a
 * statement about what each prompt shape MEASURES, not about how a screen looks. This module only
 * carries it to the renderers: one derivation per screen, then one question per card, column,
 * filter tab or metric option.
 *
 * A suppressed KPI is ABSENT — no card, no column, no option, no blank cell. A blank still occupies
 * the place where a measurement goes and reads as "we measured and found nothing", which is the
 * opposite of "this question fixed the answer in advance, so there was never anything to measure".
 */

/** The shape this module needs of a purpose tag; a tab or a fetched term both satisfy it. */
export interface PurposeKpiTermLike {
  /** `null` is how the server reports a tag written before purpose types existed. */
  purposeType?: MonitoringPromptPurposeType | null;
  purposeKpis?: readonly MonitoringPromptKpi[] | null;
}

/**
 * What one screen may show.
 *
 * `allowed === undefined` is "no mask", and it is deliberately NOT "everything is meaningful". It
 * arises in two situations that share one property — nobody has stated what the prompts are for:
 * an untyped tag (which the AI-visibility page blocks outright, with its own notice), and a screen
 * that is not scoped to a single purpose at all (the answers list on "All purposes"). Neither is a
 * claim about the KPIs, so neither may change what those screens render today.
 */
export interface PurposeKpiMask {
  readonly allowed?: ReadonlySet<MonitoringPromptKpi>;
}

/** The mask a screen with no purpose in view carries: today's behaviour, unchanged. */
export const NO_PURPOSE_KPI_MASK: PurposeKpiMask = {};

/** The catalogue order, so a rendered list of KPI names reads the same on every screen. */
const KPI_CATALOGUE: readonly MonitoringPromptKpi[] = Object.values(MonitoringPromptKpi);

/**
 * Which trend/breakdown metric each catalogue KPI stands for.
 *
 * Both spellings of the tone metric map to the same KPI, and that is not a duplicate: the purpose
 * mask is the server's published vocabulary, which carries one member for tone however this app
 * measures it (`toneMetric.ts`). A key that arrives from a stored preference or a pasted link is
 * therefore answerable whichever spelling it carries.
 */
export const AI_KPI_TO_MONITORING_KPI: Readonly<Record<AiKpi, MonitoringPromptKpi>> = {
  visibility: MonitoringPromptKpi.Visibility,
  shareOfVoice: MonitoringPromptKpi.ShareOfVoice,
  sentiment: TONE_KPI,
  verdict: TONE_KPI,
  recommendation: MonitoringPromptKpi.Recommendation,
};

/** Derive the mask once, from the purpose the screen is scoped to. */
export function purposeKpiMask(term: PurposeKpiTermLike | undefined): PurposeKpiMask {
  const allowed = allowedMonitoringPromptKpis({
    purposeType: term?.purposeType ?? undefined,
    // The helper takes a mutable array; a tab holds a readonly one.
    purposeKpis: term?.purposeKpis ? [...term.purposeKpis] : undefined,
  });

  return allowed ? { allowed: new Set(allowed) } : NO_PURPOSE_KPI_MASK;
}

/** Whether a KPI may be rendered. An unmasked screen shows everything it shows today. */
export function isKpiShown(mask: PurposeKpiMask, kpi: MonitoringPromptKpi): boolean {
  return !mask.allowed || mask.allowed.has(kpi);
}

/** The same question for a trend/breakdown metric. */
export function isMetricShown(mask: PurposeKpiMask, metric: AiKpi): boolean {
  return isKpiShown(mask, AI_KPI_TO_MONITORING_KPI[metric]);
}

/**
 * The trend/breakdown metrics this mask allows, in the order the caller offered them.
 *
 * Empty is a real answer: a purpose can allow only KPIs that no line chart plots (position and
 * attributes), and the caller then has no chart to draw rather than a chart with nothing in it.
 */
export function shownMetrics(mask: PurposeKpiMask, candidates: readonly AiKpi[]): AiKpi[] {
  return candidates.filter((metric) => isMetricShown(mask, metric));
}

/**
 * The metric a selector should hold, given what the reader last chose.
 *
 * A suppressed metric can never stay selected — it would query and plot a figure the purpose says
 * is not a measurement. The first allowed metric takes its place; with none allowed the selection is
 * left alone, because the caller is not rendering the selector at all.
 */
export function resolveShownMetric(mask: PurposeKpiMask, current: AiKpi, candidates: readonly AiKpi[]): AiKpi {
  if (isMetricShown(mask, current)) {
    return current;
  }
  return shownMetrics(mask, candidates)[0] ?? current;
}

/** "a, b and c" — the KPI names as a reader would say them. */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** A KPI's display name, lowercased to sit inside a sentence. */
function kpiSentenceName(kpi: MonitoringPromptKpi): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptKpi', kpi).toLocaleLowerCase();
}

/**
 * One line telling the reader what this purpose measures, or nothing when there is nothing to say.
 *
 * It appears exactly where a screen would otherwise lose a section without explanation, and it says
 * what IS measured rather than what is missing: a reader who came for a share-of-voice number needs
 * to know this purpose has no such number, not to be told that a card was removed.
 *
 * Silent for an untyped tag (that screen carries its own notice) and for a purpose that suppresses
 * nothing, where the line would only restate a complete screen.
 */
export function purposeKpiScopeSentence(term: PurposeKpiTermLike | undefined): string | undefined {
  const { allowed } = purposeKpiMask(term);
  if (!allowed) {
    return undefined;
  }

  if (!allowed.size) {
    return 'This purpose lists no KPIs yet. Pick them on the purpose tag to see metrics here.';
  }

  if (allowed.size === KPI_CATALOGUE.length) {
    return undefined;
  }

  const typeName = term?.purposeType
    ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptPurposeType', term.purposeType)
    : '';
  const measures = joinNames(KPI_CATALOGUE.filter((kpi) => allowed.has(kpi)).map(kpiSentenceName));

  return typeName ? `${typeName} prompts measure ${measures}.` : `These prompts measure ${measures}.`;
}
